import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Markdown } from '../Markdown';
import docco from 'react-syntax-highlighter/dist/esm/styles/hljs/docco';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  code: {
    '& pre': {
      marginBottom: theme.spacing(0.2),
      border: `1px solid ${theme.palette.divider}`,
    },
  },
  codeExecution: {
    color: theme.palette.black[300],
    fontFamily: theme.typography.fontFamilyMonospace,
    paddingTop: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
  },
  hidden: {
    display: 'none',
  },
  streamOutput: {
    paddingLeft: theme.spacing(1),
    wordWrap: 'break-word',
    fontFamily: theme.typography.fontFamilyMonospace,
    marginBottom: theme.spacing(4),
  },
  errorOutput: {
    color: theme.palette.error.main,
    paddingLeft: theme.spacing(1),
    fontFamily: theme.typography.fontFamilyMonospace,
    marginBottom: theme.spacing(4),
  },
  executionOutput: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    marginBottom: theme.spacing(4),
    fontFamily: theme.typography.fontFamilyMonospace,
  },
  outputImage: {
    maxWidth: '100%',
  },
}));

const messages = defineMessages({
  notebook: {
    id: 'codeCell.notebook',
    defaultMessage: 'Notebook',
  },
});

const Output = ({ output }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  if (output.output_type === 'stream') {
    return (
      <React.Fragment>
        <Grid item xs={1}></Grid>
        <Grid item xs={11} className={classes.streamOutput}>
          <Markdown source={output.text.join('')} />
        </Grid>
      </React.Fragment>
    );
  } else if (output.output_type === 'error') {
    return (
      <React.Fragment>
        <Grid
          container
          item
          xs={1}
          className={classes.codeExecution}
          justify="flex-end"
        >
          <FormattedMessage
            id="notebooks.codeCell.outputExecutionCount"
            defaultMessage="Error:"
            values={output}
          />
        </Grid>
        <Grid item xs={11} className={classes.errorOutput}>
          <Markdown
            source={`${output.ename}\n${output.evalue}\n${(
              output.traceback || []
            ).join('')}`}
          />
        </Grid>
      </React.Fragment>
    );
  } else if (output.output_type === 'execute_result') {
    return (
      <React.Fragment>
        <Grid
          container
          item
          xs={1}
          className={classes.codeExecution}
          justify="flex-end"
        >
          <FormattedMessage
            id="notebooks.codeCell.outputExecutionCount"
            defaultMessage="Out [{execution_count}]:"
            values={output}
          />
        </Grid>
        <Grid item xs={11} className={classes.executionOutput}>
          {output.data && output.data['text/plain'] && (
            <Markdown
              source={`\`\`\`\n${(output.data['text/plain'] || []).join(
                '',
              )}\n\`\`\``}
            />
          )}
        </Grid>
      </React.Fragment>
    );
  } else if (output.output_type === 'display_data') {
    const supportedImage = [
      'png',
      'image/png',
      'jpeg',
      'image/jpeg',
      'svg',
    ].find(i => output.data[i]);

    return (
      <React.Fragment>
        <Grid item xs={1}></Grid>
        <Grid item xs={11} className={classes.executionOutput}>
          {supportedImage && (
            <img
              alt={formatMessage(messages.notebook)}
              src={`data:${supportedImage};base64,${output.data[supportedImage]}`}
              className={classes.outputImage}
            />
          )}
          {!supportedImage && output.data['text/plain'] && (
            <Markdown
              source={`\`\`\`\n${(output.data['text/plain'] || []).join(
                '',
              )}\n\`\`\``}
            />
          )}
        </Grid>
      </React.Fragment>
    );
  }
  return <div></div>;
};

export const CodeCell = ({ cell, language, showCode }) => {
  const classes = useStyles({});

  return (
    <React.Fragment>
      <Grid
        container
        item
        xs={1}
        className={clsx(classes.codeExecution, !showCode && classes.hidden)}
        justify="flex-end"
        marginBottom={2}
      >
        <FormattedMessage
          id="notebooks.codeCell.inputExecutionCount"
          defaultMessage="In [{execution_count}]:"
          values={cell}
        />
      </Grid>
      <Grid
        item
        xs={11}
        className={clsx(classes.code, !showCode && classes.hidden)}
        marginBottom={2}
      >
        <Markdown
          source={`\`\`\`${language}\n${cell.source.join('')}\n\`\`\``}
          codeStyle={docco}
        />
      </Grid>
      {(cell.outputs || []).map((output, index) => (
        <Output key={index} output={output} />
      ))}
    </React.Fragment>
  );
};
